import { Feather } from '@expo/vector-icons';
import { FormOptionsConfig, FormValueLabel } from '@people-science/forms-core';
import React, { useMemo, useRef, useState } from 'react';
import {
  FlatList,
  LayoutRectangle,
  Modal,
  Platform,
  TextInput,
  TextInputProps,
  TextProps,
  TouchableHighlight,
  TouchableHighlightProps,
  TouchableOpacity,
  View
} from 'react-native';
import styled from 'styled-components/native';
import ListItem from '../../../components/list/ListItem';
import ListItemText from '../../../components/list/ListItemText';
import Colors from '../../../constants/Colors';
import { useDynamicFormContext } from '../../DynamicFormContext';
import { FormElementProps } from '../../FormElement';
import { FormElementPlugin } from '../FormElementPlugin';
import FormElementQuestion from './components/FormElementQuestion';
import accessibilityTags from '../../../../accessibilityTags';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const FormSelectView = styled.View`
  margin-top: 6px;
  flex: 1 1 auto;
`;

const StyledTouchableHighlight = styled.TouchableHighlight<{
  hasHelperText?: boolean;
}>`
  position: relative;
  background-color: #fff;
  border: 1px solid ${Colors.formInputBorder};
  border-radius: 8px;
  padding: 16px;
  min-height: 52px;
  margin-bottom: ${p => (p.hasHelperText ? 4 : 12)}px;
` as React.FC<
  TouchableHighlightProps & {
    hasHelperText?: boolean;
    ref?: any;
  }
>;

const StyledText = styled.Text<{
  value?: string | number | boolean;
}>`
  color: ${p =>
    p.value || typeof p.value === 'number' || typeof p.value === 'boolean'
      ? '#000'
      : Colors.placeholderColor};
  font-size: 16px;
  line-height: 18px;
  padding-right: 32px;
  padding-left: 5px;
  font-family: ${p =>
    p.value !== 0 && !p.value ? 'WorkSans-Regular' : 'DMSans-Bold'};
  max-height: 20px;
` as React.FC<
  TextProps & {
    value?: string | number | boolean;
  }
>;

const StyledDropDownIcon = styled.View`
  position: absolute;
  top: 0px;
  right: 0px;
`;

const StyledTextInput = styled.TextInput<{ empty?: boolean }>`
  background-color: ${Colors.formInputBorder};
  border: 1px solid ${Colors.formInputBorder};
  border-radius: 8px;
  padding: 16px 16px 16px 22px;
  font-size: 16px;
  max-height: 52px;
  font-family: ${p => (p.empty ? 'WorkSans-Regular' : 'DMSans-Bold')};
  font-weight: normal;
` as React.FC<
  TextInputProps & {
    ref: any;
    empty?: boolean;
  }
>;

const StyledModalContainer = styled.TouchableOpacity`
  width: 100%;
  height: 100%;
`;

const StyledPosition = styled.View<{ rect?: LayoutRectangle }>`
  position: absolute;
  top: ${p => p.rect?.y || 0}px;
  left: ${p => p.rect?.x || 0}px;
  width: ${p => p.rect?.width || 0}px;
`;

const FormPickerMenu = styled.View`
  background-color: ${Colors.lightText};
  border-radius: 8px;
  margin-top: 12px;
  box-shadow: 0 8px 8px rgba(50, 50, 71, 0.08);
  elevation: 5;
  z-index: 1;
  max-height: 200px;
`;

export type FormSelectConfig = FormOptionsConfig & {
  placeholder?: string;
};

export type FormSelectFieldProps<T> = FormElementProps<T, FormSelectConfig> & {
  value?: string | number | boolean;
};

export const FormSelectField = <T extends object>({
  stateNode,
  element,
  disabled,
  value
}: FormSelectFieldProps<T>) => {
  const scrollDeltaY = 0;
  const touchableHighlightRef = useRef<typeof TouchableHighlight>(null!);
  const textInputRef = useRef<TextInput>(null!);
  const [openMenu, setOpenMenu] = useState(false);
  const [layoutRect, setLayoutRect] = useState<LayoutRectangle>();
  const insets = useSafeAreaInsets();
  const options = useMemo<FormValueLabel[]>(
    () =>
      (element.config?.options || []).map(o =>
        ['string', 'number', 'boolean'].includes(typeof o)
          ? ({ value: o, label: o.toString() } as FormValueLabel)
          : (o as FormValueLabel)
      ),
    [element.config]
  );
  const { formValues, setFormValue } = useDynamicFormContext<
    T,
    FormSelectConfig
  >();
  const key = (element.dataSource as keyof T) || element.key;
  value = value || (formValues[key] as any);
  // console.log('select form input', value, options);
  const getOptionLabel = (value: any) =>
    // perform a `==` operator check as option values are strings and data
    // value is a number.
    options.find(o => o.value == value)?.label;
  return (
    <View>
      <FormElementQuestion element={element} />
      <FormSelectView>
        <StyledTouchableHighlight
          ref={(touchableHighlight: typeof TouchableHighlight) => {
            touchableHighlightRef.current = touchableHighlight;
          }}
          underlayColor={Colors.primary + '11'} // add alpha hex
          onPress={() => {
            (touchableHighlightRef.current as any).measureInWindow(
              (x: number, y: number, width: number, height: number) => {
                y -= scrollDeltaY;
                setLayoutRect({
                  x,
                  y: y + (Platform.OS === 'android' ? insets.top : 0),
                  width,
                  height
                });
              }
            );

            setOpenMenu(true);
          }}
        >
          <View>
            <StyledText
              value={value}
              numberOfLines={1}
              accessibilityLabel={accessibilityTags.selectorID}
              testID={accessibilityTags.selectorID}
            >
              {getOptionLabel(value) || element?.config?.placeholder || ' '}
            </StyledText>
            <StyledDropDownIcon>
              <Feather name='chevron-down' size={24} />
            </StyledDropDownIcon>
          </View>
        </StyledTouchableHighlight>
        <Modal
          transparent
          onRequestClose={() => setOpenMenu(false)}
          visible={openMenu}
        >
          <StyledModalContainer
            onPress={() => setOpenMenu(false)}
            activeOpacity={0}
          >
            <StyledPosition rect={layoutRect}>
              <TouchableOpacity onPress={() => setOpenMenu(false)}>
                <StyledTextInput
                  blurOnSubmit={false}
                  ref={(textInput: TextInput) => {
                    textInputRef.current = textInput;
                  }}
                  placeholderTextColor={Colors.placeholderColor}
                  placeholder={element?.config?.placeholder}
                  value={(
                    getOptionLabel(value) ||
                    element?.config?.placeholder ||
                    ''
                  ).toString()}
                  empty={!value}
                  editable={false}
                />
              </TouchableOpacity>
              <FormPickerMenu>
                <FlatList
                  data={options}
                  keyExtractor={item => item.value.toString()}
                  renderItem={({ item }) => (
                    <ListItem
                      onPress={() => {
                        setOpenMenu(false);
                        setFormValue(key, item.value as any, element);
                      }}
                    >
                      <ListItemText primary={item.label} />
                    </ListItem>
                  )}
                />
              </FormPickerMenu>
            </StyledPosition>
          </StyledModalContainer>
        </Modal>
      </FormSelectView>
    </View>
  );
};

export const FormSelectFieldPlugin: FormElementPlugin<any, FormSelectConfig> = {
  name: 'Dropdown',
  type: 'Select',
  dataTypes: ['text', 'integer', 'numeric', 'enum'],
  element: FormSelectField as React.FC<FormElementProps<any, FormSelectConfig>>
};
